// # Additional Revenue Analytics Component
import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../../../firebase/config';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ComposedChart
} from 'recharts';
import { 
  FaChartLine, FaGlobe, FaChartBar, 
  FaBullseye, FaMoneyBill, FaDownload, 
  FaUndo, FaTimes, FaFire
} from 'react-icons/fa';
import './net_revenue_extra.css';
import GeoAnalysis from './geonalysis';

// # Product types configuration
const productTypes = {
  'library': {
    label: 'Library Access',
    products: ['step1_library', 'step2_library']
  },
  'premium': {
    label: 'Premium Access',
    products: ['step1_premium', 'step2_premium']
  },
  'mentorship': {
    label: 'Mentorship',
    products: ['step1_mentorship', 'step2_mentorship', 'OET', 'US_VISA_Counselling']
  },
  'tutor_hosting': {
    label: 'Tutor Hosting',
    products: ['step1_tutor_hosting', 'step2_tutor_hosting']
  }
};

// # Expense categories
const expenseCategories = [
  'software maintenance',
  'hosting',
  'subscription',
  'marketing',
  'content development',
  'investment',
  'budget leftover',
  'other'
];

const NetRevenueExtra = () => {
  // # State Management
  const [activeTab, setActiveTab] = useState('geographic');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [analyticsData, setAnalyticsData] = useState({
    financialHealth: {
      netProfitMargin: [],
      burnRate: [],
      monthlyExpenses: [],
      monthlyRevenue: []
    },
    geographicData: {
      regionData: [],
      productData: []
    },
    productDuration: [],
    marketingROI: []
  });

  // # Handler Functions
  const handleExport = () => {
    try {
      // Create CSV content
      const csvContent = [
        // Headers
        ['Date', 'Revenue', 'Expenses', 'Net Revenue', 'Marketing Cost', 'ROI'].join(','),
        // Data rows
        ...analyticsData.marketingROI.map(data => [
          data.month,
          data.revenue.toFixed(2),
          data.marketingCost.toFixed(2),
          (data.revenue - data.marketingCost).toFixed(2),
          data.marketingCost.toFixed(2),
          data.roi.toFixed(2)
        ].join(','))
      ].join('\n');

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `revenue_analytics_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setShowDialog(true); // Show success dialog
    } catch (error) {
      console.error('Error exporting data:', error);
      // Handle error case
    }
  };

  const handleReset = () => {
    // Reset all filters and view settings
    setActiveTab('geographic');
    setShowDialog(true); // Show confirmation dialog
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
  };

  const handleConfirm = () => {
    // Handle dialog confirmation action
    setShowDialog(false);
  };

  // # Fetch Data
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      setLoading(true);
      try {
        const financialRef = collection(db, 'financial');
        const querySnapshot = await getDocs(financialRef);
        
        let allPayments = [];
        let allExpenses = [];
        let allSalaries = [];
        
        // # Process each document (year_month format)
        querySnapshot.docs.forEach(doc => {
          const yearMonthData = doc.data();
          
          // # Process payments
          if (yearMonthData.payments) {
            Object.entries(yearMonthData.payments).forEach(([paymentId, payment]) => {
              if (paymentId.startsWith('payment_') && payment.transaction_type === 'payment') {
                allPayments.push({
                  id: paymentId,
                  docId: doc.id,
                  timestamp: payment.metadata?.created_at || '',
                  name: payment.user?.name || 'Unknown',
                  email: payment.user?.email || '',
                  nationality: payment.user?.nationality || 'Unknown',
                  product: payment.pay?.product || 'Unknown',
                  egp_total: Number(payment.pay?.egp_total) || 0,
                  egp_net: Number(payment.pay?.egp_net) || 0,
                  budget_dvd_amount: Number(payment.budget_dvd?.egp_amount) || 0,
                  budget_dvd_percent: Number(payment.budget_dvd?.percentage_of_total) || 0,
                  duration: payment.pay?.duration || '',
                  mentor: payment.mentor?.mentor_name || '',
                  shares: payment.shares || {}
                });
              }
            });
          }
          
          // # Process expenses
          if (yearMonthData.expenses) {
            Object.entries(yearMonthData.expenses).forEach(([expenseId, expense]) => {
              if (expenseId.startsWith('expense_') && expense.transaction_type === 'expense') {
                allExpenses.push({
                  id: expenseId,
                  docId: doc.id,
                  timestamp: expense.metadata?.created_at || '',
                  category: expense.category || 'Unknown',
                  details: expense.details || '',
                  egp_total: Number(expense.egp_total) || 0,
                  admin: expense.register_admin?.name || 'Unknown'
                });
              }
            });
          }
          
          // # Process salaries
          if (yearMonthData.salaries) {
            Object.entries(yearMonthData.salaries).forEach(([salaryId, salary]) => {
              if (salaryId.startsWith('salary_')) {
                allSalaries.push({
                  id: salaryId,
                  docId: doc.id,
                  timestamp: salary.metadata?.created_at || '',
                  amount_egp: Number(salary.amount_egp) || 0,
                  notes: salary.metadata?.notes || '',
                  receiver: salary.reciever || {},
                  register_admin: salary.register_admin || {}
                });
              }
            });
          }
        });

        processAnalyticsData(allPayments, allExpenses, allSalaries);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, []);

  // # Process Analytics Data
  const processAnalyticsData = (payments, expenses, salaries) => {
    // # Process monthly data
    const monthlyData = processMonthlyData(payments, expenses, salaries);
    
    // # Process geographic data
    const geoData = processGeographicData(payments);

    // # Process duration-based data
    const durationData = processDurationData(payments);
    
    // # Process marketing ROI data
    const marketingData = processMarketingData(expenses, payments);

    setAnalyticsData({
      financialHealth: monthlyData,
      geographicData: geoData,
      productDuration: durationData,
      marketingROI: marketingData
    });
  };

  // # Process Monthly Data
  const processMonthlyData = (payments, expenses, salaries) => {
    const monthlyData = {};
    
    // # Process payments
    payments.forEach(payment => {
      const date = new Date(payment.timestamp);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          totalRevenue: 0,
          netRevenue: 0,
          budgetAmount: 0,
          expenses: 0,
          salaries: 0,
          customerCount: new Set()
        };
      }
      
      monthlyData[monthKey].totalRevenue += payment.egp_total;
      monthlyData[monthKey].netRevenue += payment.egp_net;
      monthlyData[monthKey].budgetAmount += payment.budget_dvd_amount;
      monthlyData[monthKey].customerCount.add(payment.email);
    });

    // # Process expenses
    expenses.forEach(expense => {
      const date = new Date(expense.timestamp);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          totalRevenue: 0,
          netRevenue: 0,
          budgetAmount: 0,
          expenses: 0,
          salaries: 0,
          customerCount: new Set()
        };
      }
      
      monthlyData[monthKey].expenses += expense.egp_total;
    });

    // # Process salaries
    salaries.forEach(salary => {
      const date = new Date(salary.timestamp);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          totalRevenue: 0,
          netRevenue: 0,
          budgetAmount: 0,
          expenses: 0,
          salaries: 0,
          customerCount: new Set()
        };
      }
      
      monthlyData[monthKey].salaries += salary.amount_egp;
    });

    // # Calculate metrics
    const netProfitMargin = Object.entries(monthlyData).map(([month, data]) => ({
      month,
      revenue: data.totalRevenue,
      netRevenue: data.netRevenue,
      expenses: data.expenses,
      salaries: data.salaries,
      margin: data.totalRevenue > 0 
        ? ((data.netRevenue - data.salaries) / data.totalRevenue) * 100 
        : 0
    }));

    const burnRate = Object.entries(monthlyData).map(([month, data]) => ({
      month,
      expenses: data.expenses,
      salaries: data.salaries,
      totalCosts: data.expenses + data.salaries,
      revenue: data.totalRevenue
    }));

    return {
      netProfitMargin,
      burnRate,
      monthlyRevenue: Object.entries(monthlyData).map(([month, data]) => ({
        month,
        totalRevenue: data.totalRevenue,
        netRevenue: data.netRevenue,
        budgetAmount: data.budgetAmount
      })),
      monthlyExpenses: Object.entries(monthlyData).map(([month, data]) => ({
        month,
        expenses: data.expenses,
        salaries: data.salaries
      }))
    };
  };

  // # Process Geographic Data
  const processGeographicData = (payments) => {
    // # Initialize regional data structures
    const revenueByRegion = {
      'Arab Countries': { totalRevenue: 0, netRevenue: 0, transactionCount: 0, countries: {} },
      'Asia': { totalRevenue: 0, netRevenue: 0, transactionCount: 0, countries: {} },
      'Europe': { totalRevenue: 0, netRevenue: 0, transactionCount: 0, countries: {} },
      'Americas': { totalRevenue: 0, netRevenue: 0, transactionCount: 0, countries: {} },
      'Africa': { totalRevenue: 0, netRevenue: 0, transactionCount: 0, countries: {} },
      'Oceania': { totalRevenue: 0, netRevenue: 0, transactionCount: 0, countries: {} }
    };

    const productsByRegion = {
      'Arab Countries': {},
      'Asia': {},
      'Europe': {},
      'Americas': {},
      'Africa': {},
      'Oceania': {}
    };

    // # Process payments by region
    payments.forEach(payment => {
      const nationality = payment.nationality;
      let region = 'Other';
      
      // # Determine region based on nationality
      if (nationality.match(/arab|egypt|saudi|uae|kuwait|qatar|bahrain|oman|yemen|iraq|syria|lebanon|jordan|palestine/i)) {
        region = 'Arab Countries';
      } else if (nationality.match(/china|japan|korea|india|pakistan|bangladesh|indonesia|malaysia|philippines|vietnam|thailand/i)) {
        region = 'Asia';
      } else if (nationality.match(/uk|france|germany|italy|spain|portugal|netherlands|belgium|switzerland|sweden|norway|denmark|finland/i)) {
        region = 'Europe';
      } else if (nationality.match(/usa|canada|mexico|brazil|argentina|chile|colombia|peru|venezuela/i)) {
        region = 'Americas';
      } else if (nationality.match(/nigeria|kenya|ethiopia|ghana|uganda|tanzania|south africa|morocco|algeria|tunisia/i)) {
        region = 'Africa';
      } else if (nationality.match(/australia|new zealand|fiji|papua new guinea|samoa|tonga/i)) {
        region = 'Oceania';
      }

      // # Update regional revenue data
      if (revenueByRegion[region]) {
        revenueByRegion[region].totalRevenue += payment.egp_total;
        revenueByRegion[region].netRevenue += payment.egp_net;
        revenueByRegion[region].transactionCount += 1;
        
        if (!revenueByRegion[region].countries[nationality]) {
          revenueByRegion[region].countries[nationality] = {
            totalRevenue: 0,
            netRevenue: 0,
            transactionCount: 0
          };
        }
        
        revenueByRegion[region].countries[nationality].totalRevenue += payment.egp_total;
        revenueByRegion[region].countries[nationality].netRevenue += payment.egp_net;
        revenueByRegion[region].countries[nationality].transactionCount += 1;
      }

      // # Update product data by region
      if (productsByRegion[region] && payment.product) {
        if (!productsByRegion[region][payment.product]) {
          productsByRegion[region][payment.product] = {
            count: 0,
            totalRevenue: 0,
            netRevenue: 0
          };
        }
        
        productsByRegion[region][payment.product].count += 1;
        productsByRegion[region][payment.product].totalRevenue += payment.egp_total;
        productsByRegion[region][payment.product].netRevenue += payment.egp_net;
      }
    });

    return {
      regionData: Object.entries(revenueByRegion)
        .filter(([region]) => region !== 'Other')
        .map(([region, data]) => ({
          region,
          totalRevenue: data.totalRevenue,
          netRevenue: data.netRevenue,
          transactionCount: data.transactionCount,
          countries: Object.entries(data.countries)
            .map(([country, countryData]) => ({
              country,
              totalRevenue: countryData.totalRevenue,
              netRevenue: countryData.netRevenue,
              transactionCount: countryData.transactionCount
            }))
            .sort((a, b) => b.totalRevenue - a.totalRevenue)
        }))
        .sort((a, b) => b.totalRevenue - a.totalRevenue),
      
      productData: Object.entries(productsByRegion)
        .filter(([region]) => region !== 'Other')
        .map(([region, products]) => ({
          region,
          products: Object.entries(products)
            .map(([product, data]) => ({
              product,
              count: data.count,
              totalRevenue: data.totalRevenue,
              netRevenue: data.netRevenue,
              averageRevenue: data.count > 0 ? data.totalRevenue / data.count : 0
            }))
            .sort((a, b) => b.totalRevenue - a.totalRevenue)
        })),
    };
  };

  // # Process Duration Data
  const processDurationData = (payments) => {
    const durationData = {};
    
    payments.forEach(payment => {
      const duration = payment.duration || 'Unknown';
      
      if (!durationData[duration]) {
        durationData[duration] = {
          count: 0,
          totalRevenue: 0,
          netRevenue: 0,
          budgetAmount: 0
        };
      }
      
      durationData[duration].count += 1;
      durationData[duration].totalRevenue += payment.egp_total;
      durationData[duration].netRevenue += payment.egp_net;
      durationData[duration].budgetAmount += payment.budget_dvd_amount;
    });

    return Object.entries(durationData).map(([duration, data]) => ({
      duration,
      count: data.count,
      totalRevenue: data.totalRevenue,
      netRevenue: data.netRevenue,
      budgetAmount: data.budgetAmount,
      averageRevenue: data.count > 0 ? data.totalRevenue / data.count : 0
    }));
  };

  // # Process Marketing Data
  const processMarketingData = (expenses, payments) => {
    // # Initialize monthly data structure
    const monthlyData = {};
    
    // # Process marketing expenses
    // # First, we track all marketing expenses by month
    expenses
      .filter(expense => expense.category === 'marketing')
      .forEach(expense => {
        const date = new Date(expense.timestamp);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = {
            marketingCost: 0,        // Total marketing spend for the month
            totalRevenue: 0,         // Total revenue (gross) for the month
            netRevenue: 0,           // Net revenue (after budget) for the month
            customerCount: new Set(), // Unique customers for the month
            budgetAmount: 0          // Total budget allocation for the month
          };
        }
        
        monthlyData[monthKey].marketingCost += expense.egp_total;
      });

    // # Process revenue data
    // # Track all revenue metrics by month
    payments.forEach(payment => {
      const date = new Date(payment.timestamp);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          marketingCost: 0,
          totalRevenue: 0,
          netRevenue: 0,
          customerCount: new Set(),
          budgetAmount: 0
        };
      }
      
      monthlyData[monthKey].totalRevenue += payment.egp_total;
      monthlyData[monthKey].netRevenue += payment.egp_net;
      monthlyData[monthKey].budgetAmount += payment.budget_dvd_amount;
      monthlyData[monthKey].customerCount.add(payment.email);
    });

    // # Calculate ROI and metrics
    // # For each month, we calculate:
    // # 1. ROI = ((Total Revenue - Marketing Cost) / Marketing Cost) * 100
    // # 2. Cost per customer = Marketing Cost / Number of Unique Customers
    // # 3. Revenue per marketing spend = Total Revenue / Marketing Cost
    return Object.entries(monthlyData).map(([month, data]) => {
      // # Calculate ROI only if there was marketing spend
      const roi = data.marketingCost > 0 
        ? ((data.totalRevenue - data.marketingCost) / data.marketingCost) * 100 
        : 0;

      // # Calculate cost per customer only if there were customers
      const costPerCustomer = data.customerCount.size > 0 
        ? data.marketingCost / data.customerCount.size 
        : 0;

      // # Calculate revenue per marketing spend
      const revenuePerMarketingSpend = data.marketingCost > 0
        ? data.totalRevenue / data.marketingCost
        : 0;

      return {
        month,
        marketingCost: data.marketingCost,
        totalRevenue: data.totalRevenue,
        netRevenue: data.netRevenue,
        budgetAmount: data.budgetAmount,
        roi,                           // ROI percentage
        customerCount: data.customerCount.size,
        costPerCustomer,              // Cost to acquire each customer
        revenuePerMarketingSpend,     // Revenue generated per marketing EGP spent
        // # Add detailed metrics for transparency
        metrics: {
          roiCalculation: {
            formula: "((Total Revenue - Marketing Cost) / Marketing Cost) * 100",
            totalRevenue: data.totalRevenue,
            marketingCost: data.marketingCost,
            difference: data.totalRevenue - data.marketingCost,
            result: roi
          },
          costPerCustomerCalculation: {
            formula: "Marketing Cost / Number of Unique Customers",
            marketingCost: data.marketingCost,
            uniqueCustomers: data.customerCount.size,
            result: costPerCustomer
          },
          revenuePerSpendCalculation: {
            formula: "Total Revenue / Marketing Cost",
            totalRevenue: data.totalRevenue,
            marketingCost: data.marketingCost,
            result: revenuePerMarketingSpend
          }
        }
      };
    });
  };

  // # Update the marketing stats section in the render to show the new metrics
  const renderMarketingStats = (data) => {
    return (
      <div className="fnc_rev_extra_marketing_stats">
        <div className="fnc_rev_extra_marketing_card">
          <FaMoneyBill className="fnc_rev_extra_marketing_icon" />
          <h4>Total Marketing Spend</h4>
          <p className="fnc_rev_extra_marketing_value">
            E£ {data.reduce((sum, m) => sum + m.marketingCost, 0).toLocaleString()}
          </p>
          <p className="fnc_rev_extra_marketing_subtitle">
            Total investment in marketing activities
          </p>
        </div>
        
        <div className="fnc_rev_extra_marketing_card">
          <FaFire className="fnc_rev_extra_marketing_icon" />
          <h4>Average ROI</h4>
          <h5>ROI = ((Total Revenue - Marketing Cost) / Marketing Cost) × 100</h5>
          <p className="fnc_rev_extra_marketing_value">
            {(data.reduce((sum, m) => sum + m.roi, 0) / data.length).toFixed(2)}%
          </p>
          <p className="fnc_rev_extra_marketing_subtitle">
            Return on Marketing Investment
          </p>
        </div>

        <div className="fnc_rev_extra_marketing_card">
          <FaMoneyBill className="fnc_rev_extra_marketing_icon" />
          <h4>Revenue Per Marketing EGP</h4>
          <p className="fnc_rev_extra_marketing_value">
            E£ {(data.reduce((sum, m) => sum + m.revenuePerMarketingSpend, 0) / data.length).toFixed(2)}
          </p>
          <p className="fnc_rev_extra_marketing_subtitle">
            Average revenue generated per EGP spent on marketing
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="fnc_rev_extra_container">
      {/* # Tabs Navigation */}
      <div className="fnc_rev_extra_tabs">
        <button 
          className={`fnc_rev_extra_tab ${activeTab === 'geographic' ? 'active' : ''}`}
          onClick={() => setActiveTab('geographic')}
        >
          <FaGlobe /> Geographic Analysis
        </button>
        <button 
          className={`fnc_rev_extra_tab ${activeTab === 'marketing' ? 'active' : ''}`}
          onClick={() => setActiveTab('marketing')}
        >
          <FaBullseye /> Marketing ROI
        </button>
      </div>

      {/* # Content Area */}
      <div className="fnc_rev_extra_content">
        {loading ? (
          <div className="fnc_rev_extra_loading">Loading analytics data...</div>
        ) : (
          <>
            {/* # Geographic Analysis Tab */}
            {activeTab === 'geographic' && (
              <GeoAnalysis analyticsData={analyticsData.geographicData} />
            )}

            {/* # Marketing ROI Tab */}
            {activeTab === 'marketing' && (
              <div className="fnc_rev_extra_section">
                {/* Marketing ROI Stats */}
                {renderMarketingStats(analyticsData.marketingROI)}

                {/* Marketing ROI Charts */}
                <div className="fnc_rev_extra_chart_container">
                  <h3>Marketing ROI Over Time</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={analyticsData.marketingROI}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="money" />
                      <YAxis yAxisId="percentage" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Line 
                        yAxisId="money"
                        type="monotone"
                        dataKey="marketingCost" 
                        stroke="#ff8042" 
                        name="Marketing Cost (EGP)"
                        dot={{ r: 4 }}
                      />
                      <Line 
                        yAxisId="money"
                        type="monotone"
                        dataKey="totalRevenue" 
                        stroke="#82ca9d" 
                        name="Revenue (EGP)"
                        dot={{ r: 4 }}
                      />
                      <Line 
                        yAxisId="percentage"
                        type="monotone" 
                        dataKey="roi" 
                        stroke="#8884d8" 
                        name="ROI %"
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="fnc_rev_extra_chart_container">
                  <h3>Customer Acquisition Cost Analysis</h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={analyticsData.marketingROI}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="cost" />
                      <YAxis yAxisId="count" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Line 
                        yAxisId="cost"
                        type="monotone"
                        dataKey="costPerCustomer" 
                        stroke="#8884d8" 
                        name="Cost per Customer (EGP)"
                        dot={{ r: 4 }}
                      />
                      <Line 
                        yAxisId="count"
                        type="monotone" 
                        dataKey="customerCount" 
                        stroke="#82ca9d" 
                        name="Customer Count"
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Control Buttons */}
            <div className="fnc_rev_extra_control-group">
              <button className="fnc_rev_extra_button" onClick={handleExport}>
                <FaDownload /> Export Data
              </button>
              <button className="fnc_rev_extra_button secondary" onClick={handleReset}>
                <FaUndo /> Reset View
              </button>
            </div>

            {/* Dialog */}
            {showDialog && (
              <div className="fnc_rev_extra_dialog-overlay" onClick={handleCloseDialog}>
                <div className="fnc_rev_extra_dialog" onClick={e => e.stopPropagation()}>
                  <div className="fnc_rev_extra_dialog-header">
                    <h3>Dialog Title</h3>
                    <button 
                      className="fnc_rev_extra_dialog-close"
                      onClick={handleCloseDialog}
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <div className="fnc_rev_extra_dialog-content">
                    {/* Dialog content */}
                  </div>
                  <div className="fnc_rev_extra_dialog-actions">
                    <button className="fnc_rev_extra_button secondary" onClick={handleCloseDialog}>
                      Cancel
                    </button>
                    <button className="fnc_rev_extra_button" onClick={handleConfirm}>
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NetRevenueExtra;
